import { supabase } from './supabase';
import { SCREENING_KINDS, checkTimestamp, proofRows, screeningPatch } from './screeningChecks';

export { SCREENING_KINDS } from './screeningChecks';

// Extracted from OnboardingPipeline.js (2026-08-08) so the background-check/
// clinical-cert rules and mutations are usable from anywhere a staff member
// needs them tracked — not just the onboarding kanban. Originally these
// lived only in OnboardingPipeline.js, which excludes active staff from its
// queries entirely, so a fast-tracked or already-active staff member had no
// way to start/track GCHEXS or request a cert. PersonnelProfile.js's
// compliance panels (src/components/hr/StaffCompliancePanels.js) and
// OnboardingPipeline.js's existing pipeline UI both call into this file now,
// so the actual database-mutation logic exists in exactly one place.

// RN/LPN are exempt from GCHEXS under Georgia SB 406 — their license IS the
// check (Policy A-2.1). Everyone else needs the real GCHEXS/IdentoGO process.
export const bgCheckTypeFor = (s) => ['RN', 'LPN'].includes(s.position_code) ? 'license_verification' : 'gchexs';

export const PROVISIONAL_DAYS = 30;
export const provisionalExpiry = (initiatedAt) => {
  if (!initiatedAt) return null;
  const d = new Date(initiatedAt);
  d.setDate(d.getDate() + PROVISIONAL_DAYS);
  return d;
};
export const daysUntil = (date) => date ? Math.ceil((date.getTime() - Date.now()) / 86400000) : null;

// Per SOP-HR01, CPR/First Aid and TB screening are onboarding-verification
// items for direct-care roles. Reuses staff_certifications (the same table
// Training & Compliance's renewal tracking already watches).
export const CLINICAL_CERT_TYPES = [
  { key: 'cpr_first_aid', label: 'CPR / First Aid', labelEs: 'RCP / Primeros Auxilios', months: 24 },
  { key: 'tb_screening', label: 'TB Screening', labelEs: 'Prueba de Tuberculosis (TB)', months: 12 },
];
export const needsClinicalCerts = (s) => ['RN', 'LPN', 'PCA', 'BHA'].includes(s.position_code);

// ── Background check mutations (parallel, non-blocking — see Policy A-2.1) ──
// Each returns the patch that was applied to `staff` (so a caller can merge
// it into local state) and throws on error, matching the original inline
// try/catch-in-the-caller pattern this was extracted from.

export async function startGchexsCheck(staffId, provisionalSupervisorId, logEvent) {
  const now = new Date().toISOString();
  const patch = { bg_check_type: 'gchexs', bg_check_status: 'pending', bg_check_initiated_at: now, provisional_supervisor_id: provisionalSupervisorId || null };
  const { error } = await supabase.from('staff').update(patch).eq('id', staffId);
  if (error) throw error;
  if (logEvent) await logEvent(staffId, 'bg_check_started', 'GCHEXS background check initiated', null, null);
  return patch;
}

export async function clearBackgroundCheck(staff, logEvent) {
  const now = new Date().toISOString();
  const patch = { bg_check_status: 'cleared', bg_check_cleared_at: now };
  const { error } = await supabase.from('staff').update(patch).eq('id', staff.id);
  if (error) throw error;
  if (staff.bg_check_type === 'gchexs') {
    const isAgency = staff.employment_type === 'agency';
    const issued = now.slice(0, 10);
    const expiry = new Date(); expiry.setMonth(expiry.getMonth() + 60);
    const { error: certErr } = await supabase.from('staff_certifications').insert([{
      staff_id: staff.id, cert_type: 'gcic_background', cert_label: 'GCIC/FBI Background Check',
      issued_date: issued, expiry_date: expiry.toISOString().slice(0, 10),
      verifying_party: isAgency ? 'sending_agency' : 'ahfc_direct',
      notes: isAgency ? 'Filed from sending agency documentation (Policy A-2.4)' : 'Cleared via Personnel record',
    }]);
    if (certErr) throw certErr;
  }
  if (logEvent) await logEvent(staff.id, 'bg_check_cleared', 'Background check cleared', null, null);
  return patch;
}

export async function flagBackgroundCheck(staffId, logEvent) {
  const patch = { bg_check_status: 'flagged' };
  const { error } = await supabase.from('staff').update(patch).eq('id', staffId);
  if (error) throw error;
  if (logEvent) await logEvent(staffId, 'bg_check_flagged', 'Background check flagged/unsatisfactory', null, null);
  return patch;
}

// license_verified_at/by is not a duplicate of bg_check_cleared_at. The GCHEXS
// exemption for licensed nurses (O.C.G.A. § 31-7-350 et seq.) holds only while
// the Georgia license is current and in good standing, so the date we last
// confirmed that, and who confirmed it, is the evidence the exemption rests on,
// and an auditor will ask for it by name.
export async function markLicenseVerified(staffId, logEvent, verifiedBy) {
  return recordScreeningCheck(staffId, 'license', verifiedBy, logEvent);
}

// The statutory "registry check" is the nurse aide registry, the state sexual
// offender registry and the LEIE; Georgia Medicaid separately requires
// exclusion screening (GA OIG + LEIE + SAM) before hire and monthly after.
// Both stay with AHFC as the enrolled provider. What each check is, and how a
// person's record of it reads, is lib/screeningChecks.js.
//
// proofs: [{ name, url }], the Drive files that show it. checkedOn: the day it
// was done, 'YYYY-MM-DD', when that is not today. The proof goes in first: a
// failure then leaves a stray file record, never a check with its proof lost.
export async function recordScreeningCheck(staffId, kind, checkedBy, logEvent, { checkedOn, proofs = [] } = {}) {
  const spec = SCREENING_KINDS[kind];
  if (!spec) throw new Error(`Unknown screening kind: ${kind}`);
  const at = checkTimestamp(checkedOn);
  if (proofs.length) {
    const { error: proofErr } = await supabase.from('staff_documents').insert(proofRows(staffId, kind, at, proofs));
    if (proofErr) throw proofErr;
  }
  const patch = screeningPatch(kind, at, checkedBy);
  const { error } = await supabase.from('staff').update(patch).eq('id', staffId);
  if (error) throw error;
  if (logEvent) {
    const proved = proofs.length ? ` · ${proofs.length} proof file${proofs.length === 1 ? '' : 's'} attached` : ' · no proof attached';
    await logEvent(staffId, spec.eventType, `${spec.detail} on ${at.slice(0, 10)}${checkedBy ? ` by ${checkedBy}` : ''}${proved}`, null, null);
  }
  return patch;
}

// Generic onboarding_events logger — same shape OnboardingPipeline.js's
// local logEvent uses, exposed here so callers outside that page (like
// PersonnelProfile.js) can pass it through to the mutations above.
export async function logOnboardingEvent(staffId, eventType, detail, previousStatus, newStatus, actorName) {
  await supabase.from('onboarding_events').insert({
    staff_id: staffId, event_type: eventType, event_detail: detail,
    previous_status: previousStatus, new_status: newStatus, actor: actorName || 'system',
  });
}

// ── Scheduling guard (2026-09-08) ─────────────────────────────────────────
// One-email onboarding made every post-signature requirement a follow-up
// rather than a gate. Two things still must stop someone being put on a
// client (Policy A-2.1): a FLAGGED background check, and a GCHEXS check
// still pending after the 30-day provisional window. Everything else here
// is a warning the scheduler confirms past. Requires the staff row to carry
// SCHEDULING_GUARD_FIELDS.
export const SCHEDULING_GUARD_FIELDS = 'bg_check_status, bg_check_type, bg_check_initiated_at, position_code';

export function schedulingGuard(s) {
  if (!s) return null;
  const type = s.bg_check_type || bgCheckTypeFor(s);
  if (s.bg_check_status === 'cleared') return null;
  if (s.bg_check_status === 'flagged') {
    return { level: 'block', label: 'Background check FLAGGED', detail: 'Unsatisfactory determination — do not assign client care (Policy A-2.1).' };
  }
  if (type === 'license_verification') {
    return { level: 'warn', label: 'License not verified', detail: 'RN/LPN license has not been verified yet — verify it on Nursys and mark it on their Personnel File.' };
  }
  if (s.bg_check_status === 'pending') {
    const left = daysUntil(provisionalExpiry(s.bg_check_initiated_at));
    if (left != null && left <= 0) {
      return { level: 'block', label: 'Provisional window expired', detail: 'GCHEXS is still pending and the 30-day provisional start has lapsed (Policy A-2.1). Chase the result before assigning.' };
    }
    return { level: 'warn', label: `GCHEXS pending (${left != null ? `${left} day${left === 1 ? '' : 's'} left` : 'provisional'})`, detail: 'Provisional start — must work under direct supervision until GCHEXS clears.' };
  }
  return { level: 'warn', label: 'GCHEXS not started', detail: 'No background check has been submitted — provisional supervision required and the 30-day window has not begun.' };
}

// Small helper for pickers: "⛔" for a block, "⚠" for a warning, '' otherwise.
export const schedulingGuardMark = (s) => { const g = schedulingGuard(s); return g ? (g.level === 'block' ? ' ⛔' : ' ⚠') : ''; };
